import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiMapPin, FiCalendar, FiClock, FiExternalLink, FiPrinter, FiDownload, FiShare2, FiMail, FiUserCheck, FiInfo } from 'react-icons/fi';
import { useLanguage } from './Header';
import { getTranslation, Language } from '../translations';
import Confetti from 'react-confetti';

// Local helper to safely render react-icons with TS across versions
const renderIcon = (Icon: any) => React.createElement(Icon as React.ComponentType);

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: clamp(7rem, 8vw, 8.5rem);
  padding-bottom: 2rem;
  background: transparent;
  position: relative;
  z-index: 10;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 1.5rem;
    padding-top: 6.5rem;
    padding-bottom: 1.5rem;
  }

  @media (max-width: 640px) {
    padding: 1.5rem;
    padding-top: 6rem;
    padding-bottom: 1rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    padding-top: 5.5rem;
    padding-bottom: 1rem;
  }
`;



const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const BackButton = styled.button`
  background: #f8f9fa;
  color: #007aff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 20;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f5;
    transform: translateX(-2px);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    top: 1.25rem;
    left: 1.25rem;
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    top: 1rem;
    left: 1rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    gap: 0.375rem;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const HeaderBox = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border-radius: 28px;
  padding: 2.75rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e8eef5;
  box-shadow: 0 12px 30px rgba(2, 6, 23, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  position: relative;
  text-align: center;
  overflow: hidden;

  /* Corner light accents */
  &::before,
  &::after {
    content: '';
    position: absolute;
    pointer-events: none;
    border-radius: 50%;
    filter: blur(44px);
    opacity: 0.35;
  }
  &::before {
    width: 360px;
    height: 160px;
    top: -80px;
    left: -60px;
    background: radial-gradient(120% 100% at 0% 0%, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0) 70%);
  }
  &::after {
    width: 360px;
    height: 160px;
    top: -80px;
    right: -60px;
    background: radial-gradient(120% 100% at 100% 0%, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0) 70%);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
    border-radius: 20px;
    margin-bottom: 1.5rem;
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
    border-radius: 18px;
    margin-bottom: 1.25rem;
    gap: 0.875rem;
  }
  
  .dark & {
    background: linear-gradient(180deg, #0f1116 0%, #0c1118 100%);
    border-color: #253042;
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.5);
  }
`;

const HeaderContent = styled.div`
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

/* Animated success icon */
const SuccessIcon = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.5rem;
  box-shadow: 0 16px 36px rgba(16, 185, 129, 0.28);
  position: relative;
  isolation: isolate;

  /* Soft outer glow ring */
  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: inherit;
    background: radial-gradient(50% 50% at 50% 50%, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0) 70%);
    z-index: -1;
  }

  /* Pop-in animation */
  animation: pop-in 440ms cubic-bezier(0.2, 0.8, 0.2, 1) 60ms both;

  @keyframes pop-in {
    0% { transform: scale(0.85); opacity: 0; }
    60% { transform: scale(1.06); opacity: 1; }
    100% { transform: scale(1); }
  }

  @media (max-width: 768px) {
    width: 72px;
    height: 72px;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 480px) {
    width: 64px;
    height: 64px;
    margin-bottom: 0.5rem;
  }
`;

/* Shimmer accent over the title */
const SuccessTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.25;
  text-align: center;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    line-height: 1.3;
  }
`;

const ContentContainer = styled.div`
  margin: 0 auto;
  padding: 0;
  
  @media (max-width: 768px) {
    padding: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  line-height: 1.3;
  text-align: center;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const SubtleText = styled.p`
  font-size: 0.9rem;
  color: #718096;
  margin: 0.5rem 0 0 0;
  font-weight: 400;
  letter-spacing: 0.2px;
  opacity: 0.85;
  line-height: 1.5;
`;

const SubTitle = styled.h2`
  font-size: 1.5rem;
  color: #4a5568;
  margin: 1rem 0 1.5rem 0;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  letter-spacing: -0.01em;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const Info = styled.p`
  font-size: 1.1rem;
  color: #2d3748;
  margin-bottom: 1rem;
  line-height: 1.6;
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const OrderNumber = styled.span`
  font-weight: 600;
  color: #0099ff;
`;

const CustomerRef = styled.div`
  display: inline-flex;
  align-items: center;
  background: #f5f5f7;
  color: #1d1d1f;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: 1px solid #e5e5e7;
  box-shadow: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ebebed;
    border-color: #d1d1d6;
  }
`;

const HomeButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #0099ff;
  color: white;
  text-decoration: none;
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  margin-top: 2rem;
  box-shadow: 0 2px 4px rgba(0, 153, 255, 0.2);
`;

const DhlLink = styled.a`
  color: #0099ff;
  font-weight: 600;
  text-decoration: none;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 0;
  margin-bottom: 2rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  }
  
  .dark & {
    background: #111315;
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    border-radius: 14px;
    margin-bottom: 1.25rem;
  }
`;

const UnifiedCardHeader = styled.div`
  position: relative;
  padding: 1.75rem 2rem 1.5rem 2rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.06);
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  
  .dark & { 
    background: linear-gradient(180deg, #121417 0%, #0f1215 100%); 
    border-bottom: 1px solid rgba(255, 255, 255, 0.08); 
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem 1rem 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 1rem 0.875rem 1rem;
  }
`;

const HeaderCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const HeaderActionsRight = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }
`;

const UnifiedCardBody = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 1rem;
  }
`;

// Calendar add-to-agenda area
const CalendarActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const SuccessChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #bbf7d0;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.01em;

  svg { width: 16px; height: 16px; }

  .dark & { background: rgba(16,185,129,0.12); border-color: rgba(16,185,129,0.3); color: #34d399; }
`;

const CalendarButtons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

const CalendarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #ffffff;
  color: #1d1d1f;
  border: 1px solid rgba(17, 24, 39, 0.12);
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover { 
    background: #f8fafc; 
    transform: translateY(-2px); 
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: rgba(17, 24, 39, 0.18);
  }
  
  &:active {
    transform: translateY(0);
  }

  img { width: 20px; height: 20px; object-fit: contain; }

  .dark & { 
    background: #1a1d23; 
    border-color: rgba(255, 255, 255, 0.12); 
    color: #e5e7eb; 
  }
  
  .dark &:hover { 
    background: #23262d;
    border-color: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 768px) {
    padding: 9px 14px;
    font-size: 0.875rem;
    img { width: 18px; height: 18px; }
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    min-height: 44px;
    
    &:hover {
      transform: none;
    }

    img { width: 16px; height: 16px; }
  }
`;

// Compact inline actions area under header
const TopActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 0 1rem 0;
`;

const InfoPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
  
  .dark & { background: #0f1216; border-color: #253042; color: #cbd5e1; }
`;

const SuccessCenterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #065f46;
  border: 1.5px solid #10b981;
  border-radius: 9999px;
  font-weight: 700;
  letter-spacing: -0.01em;
  font-size: 0.95rem;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);

  svg { width: 18px; height: 18px; }

  .dark & { 
    background: linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.10) 100%); 
    color: #34d399; 
    border-color: rgba(16,185,129,0.4); 
    box-shadow: 0 4px 12px rgba(0,0,0,0.3); 
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.875rem;
    gap: 8px;
    svg { width: 16px; height: 16px; }
  }

  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 0.8125rem;
    gap: 6px;

    svg { width: 14px; height: 14px; }
  }
`;

const ChipIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(16,185,129,0.25);
  svg { width: 12px; height: 12px; stroke-width: 3; }
`;

const HeaderHint = styled.div`
  margin-top: 8px;
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 600;
  letter-spacing: -0.01em;
  
  .dark & { 
    color: #94a3b8; 
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    margin-top: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-top: 0.375rem;
    padding: 0 0.5rem;
  }
`;

// Improved Approval Banner
const ApprovalBanner = styled.div`
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%);
  border: 2px solid #10b981;
  border-radius: 20px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #10b981, #059669, #047857);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -10px;
    width: 100px;
    height: 200%;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { transform: translateX(0) rotate(0deg); opacity: 0.5; }
    50% { transform: translateX(20px) rotate(5deg); opacity: 0.8; }
  }
  
  .dark & {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.10) 50%, rgba(16, 185, 129, 0.05) 100%);
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const ApprovalContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 2;
`;

const ApprovalIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
  animation: bounce 2s ease-in-out infinite;
  
  svg { width: 30px; height: 30px; stroke-width: 3; }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-6px); }
    60% { transform: translateY(-3px); }
  }
`;

const ApprovalText = styled.div`
  flex: 1;
`;

const ApprovalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 800;
  color: #065f46;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
  
  .dark & { color: #34d399; }
`;

const ApprovalSubtitle = styled.p`
  font-size: 1.1rem;
  color: #047857;
  margin: 0;
  font-weight: 500;
  
  .dark & { color: #6ee7b7; }
`;

// Enhanced Checklist
const ChecklistContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  .dark & {
    background: #0f1419;
    border-color: #1e293b;
  }
`;

const ChecklistTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg { width: 20px; height: 20px; color: #3b82f6; }
  
  .dark & { color: #e2e8f0; }
`;

const ChecklistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
  
  .dark & {
    background: #111827;
    border-color: #374151;
  }
  
  .dark &:hover {
    background: #1f2937;
    border-color: #4b5563;
  }
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  
  svg { width: 14px; height: 14px; stroke-width: 3; }
`;

const CheckText = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
  
  .dark & { color: #d1d5db; }
`;



// Define interfaces for styled component props
interface MethodBadgeProps {
  color?: string;
  textColor?: string;
}

const MethodBadge = styled.div<MethodBadgeProps>`
  display: inline-flex;
  align-items: center;
  background: ${props => props.color || '#f0fdf4'};
  color: ${props => props.textColor || '#16a34a'};
  font-size: 0.85rem;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid ${props => props.textColor ? `${props.textColor}20` : '#16a34a20'};
  box-shadow: none;
  transition: all 0.2s ease;
  letter-spacing: 0.01em;
  
  &:hover {
    background: ${props => props.color ? `${props.color}cc` : '#ecfdf5'};
    border-color: ${props => props.textColor ? `${props.textColor}40` : '#16a34a40'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eef2f5;
  margin: 1.25rem 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(200px, 1fr));
  gap: 1rem;
  
  @media (min-width: 1200px) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }

  @media (max-width: 480px) {
    gap: 0.625rem;
  }
`;

const SummaryItem = styled.div`
  background: #ffffff;
  border: 1.5px solid rgba(17, 24, 39, 0.1);
  border-radius: 16px;
  padding: 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  min-height: 110px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: rgba(17, 24, 39, 0.15);
  }
  
  .dark & {
    background: #1a1d23;
    border-color: rgba(255, 255, 255, 0.12);
  }
  
  .dark &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem;
    min-height: 100px;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.875rem;
    min-height: auto;
    border-radius: 14px;
    gap: 0.625rem;
    
    &:hover {
      transform: none;
    }
  }
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  
  svg {
    width: 16px;
    height: 16px;
    color: #475569;
  }
  
  .dark & {
    color: #94a3b8;
    
    svg {
      color: #64748b;
    }
  }
`;

const SummaryValue = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.2;
  
  .dark & {
    color: #f1f5f9;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.15rem;
  }
`;

const SummarySubValue = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  margin-top: 0.25rem;
  line-height: 1.4;
  
  .dark & {
    color: #94a3b8;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 0.625rem;
  }
`;

const NoPrint = styled.div`
  @media print {
    display: none !important;
  }
`;

const PrimaryButton = styled.button`
  background: #0f172a;
  color: #ffffff;
  border: 1px solid #0f172a;
  border-radius: 12px;
  padding: 1rem 1.75rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  letter-spacing: -0.01em;

  &:hover {
    background: #1e293b;
    border-color: #1e293b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  .dark & {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #f1f5f9;
    box-shadow: 0 2px 8px rgba(241, 245, 249, 0.15);
    
    &:hover {
      background: #e2e8f0;
      border-color: #e2e8f0;
      box-shadow: 0 4px 12px rgba(241, 245, 249, 0.2);
    }
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    min-height: 44px;
    
    &:hover {
      transform: none;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const GhostButton = styled.button`
  background: #ffffff;
  color: #374151;
  border: 1px solid rgba(17, 24, 39, 0.12);
  border-radius: 10px;
  padding: 0.625rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: rgba(17, 24, 39, 0.18);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  
  .dark & {
    background: #1a1d23;
    color: #e5e7eb;
    border-color: rgba(255, 255, 255, 0.12);
  }
  
  .dark &:hover {
    background: #23262d;
    border-color: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    gap: 0.375rem;
    min-height: 44px;

    &:hover {
      transform: none;
    }
  }
`;

const Subtle = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const InfoCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.875rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.625rem;
    margin-bottom: 1.25rem;
  }
`;

const InfoCard = styled.div`
  padding: 1.75rem 1.5rem;
  background: #ffffff;
  border-radius: 16px;
  border: 1.5px solid rgba(17, 24, 39, 0.1);
  text-align: center;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 140px;
  
  &:hover {
    background: #ffffff;
    border-color: rgba(17, 24, 39, 0.15);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
  
  .dark & {
    background: #1a1d23;
    border-color: rgba(255, 255, 255, 0.12);
  }
  
  .dark &:hover {
    background: #23262d;
    border-color: rgba(255, 255, 255, 0.18);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.125rem;
    min-height: 120px;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.875rem;
    min-height: auto;
    border-radius: 14px;
    
    &:hover {
      transform: none;
    }
  }
`;

const InfoCardLabel = styled.div`
  font-size: 0.6875rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.8;
  }
  
  .dark & {
    color: #94a3b8;
  }
`;

const InfoCardValue = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 0.625rem;
  letter-spacing: -0.02em;
  line-height: 1.3;
  
  .dark & {
    color: #f1f5f9;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const InfoCardSubValue = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  font-weight: 500;
  line-height: 1.4;
  
  .dark & {
    color: #94a3b8;
  }
`;

const InfoCardHint = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  line-height: 1.5;
  padding-top: 0.875rem;
  border-top: 1px solid rgba(17, 24, 39, 0.06);
  
  svg {
    width: 14px;
    height: 14px;
    margin-top: 2px;
    flex-shrink: 0;
    opacity: 0.7;
  }
  
  .dark & {
    color: #94a3b8;
    border-top-color: rgba(255, 255, 255, 0.08);
  }
`;

const DetailBox = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;
  min-height: 80px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #e0e0e0;
  }
`;

const DetailLabel = styled.p`
  font-size: 0.8rem;
  color: #8e8e93;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  opacity: 1;
  line-height: 1.2;
`;

const DetailValue = styled.p`
  font-size: 1.15rem;
  color: #1d1d1f;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CalendarStatusContainer = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const CalendarStatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const CalendarStatusIcon = styled.div<{ status: 'idle' | 'success' | 'error' | 'loading' }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 600;
  
  ${props => {
    switch (props.status) {
      case 'success':
        return 'background: #10b981;';
      case 'error':
        return 'background: #ef4444;';
      case 'loading':
        return 'background: #6b7280; animation: spin 1s linear infinite;';
      default:
        return 'background: #6b7280;';
    }
  }}
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CalendarStatusTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const CalendarStatusMessage = styled.p`
  font-size: 0.95rem;
  color: #4a5568;
  margin: 0;
  line-height: 1.5;
`;

const CalendarIcons = {
  success: '✓',
  error: '✗',
  loading: '⟳',
  idle: '📅'
};

interface SuccessPageProps {
  orderNumber?: string;
  email?: string;
  onBackToHome: () => void;
  deliveryMethod?: 'store' | 'home';
  customerName?: {
    firstName: string;
    lastName: string;
  };
  appointment?: {
    location?: string;
    date?: string;
    time?: string;
    mapsUrl?: string;
    address?: string;
  };
  calendarEventStatus?: 'idle' | 'success' | 'error';
  calendarEventMessage?: string;
  isCreatingCalendarEvent?: boolean;
}

const SuccessPage: React.FC<SuccessPageProps> = ({ 
  orderNumber = "lab9_7623", 
  email = "e@gmail.com",
  onBackToHome,
  deliveryMethod = 'store',
  customerName = { firstName: '', lastName: '' },
  appointment = {},
  calendarEventStatus = 'idle',
  calendarEventMessage = '',
  isCreatingCalendarEvent = false
}) => {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language as Language, key);
  const [recycle, setRecycle] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecycle(false);
    }, 5000); // Confetti will stop after 5 seconds

    return () => clearTimeout(timer);
  }, []);

  // Generate a unique customer reference number based on order number
  const customerRef = orderNumber ? `#${orderNumber.replace('PB', '')}` : `#${Math.floor(1000 + Math.random() * 9000)}`;
  
  // Format customer name for title
  const fullName = customerName.firstName && customerName.lastName 
    ? `${customerName.firstName} ${customerName.lastName}`
    : '';
  
  const titleWithName = fullName 
    ? `${t('allDone')} ${fullName}`
    : t('allDone');

  // Set colors based on delivery method
  const methodColors = {
    store: {
      bg: '#e6f7ee',
      text: '#00a86b'
    },
    home: {
      bg: '#e1f0ff',
      text: '#0099ff'
    }
  };

  return (
    <Container>
      {showConfetti && (
        <Confetti
          recycle={recycle}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
      <ContentContainer>
        <SectionCard>
          <UnifiedCardHeader>
            <HeaderCenter>
              <SuccessCenterChip>
                <ChipIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </ChipIcon>
                {t('successConfirmed')}
              </SuccessCenterChip>
              <HeaderHint>{t('successAddCalendar')}</HeaderHint>
              <CalendarButtons>
                <CalendarButton onClick={() => {
                  if (!(appointment.date && appointment.time)) return;
                  // Parse DD/MM/YYYY format into YYYY-MM-DD for valid Date construction
                  const [dd, mm, yyyy] = appointment.date.split('/');
                  const startDate = new Date(`${yyyy}-${mm}-${dd}T${appointment.time}:00`);
                  if (isNaN(startDate.getTime())) return;
                  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
                  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(t('calendarEventTitle'))}&dates=${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z&details=${encodeURIComponent(t('calendarEventDescription'))}&location=${encodeURIComponent(appointment.location || '')}`;
                  window.open(url, '_blank');
                }}>
                  <img src="/google-calendar.png" alt="Google Calendar" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                  Google Calendar
                </CalendarButton>
                <CalendarButton onClick={() => {
                  if (!(appointment.date && appointment.time)) return;
                  // Parse DD/MM/YYYY format into YYYY-MM-DD for valid Date construction
                  const [dd, mm, yyyy] = appointment.date.split('/');
                  const startDate = new Date(`${yyyy}-${mm}-${dd}T${appointment.time}:00`);
                  if (isNaN(startDate.getTime())) return;
                  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
                  const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Phone Bros//Appointment//EN\nBEGIN:VEVENT\nUID:${Date.now()}@phonebros.be\nDTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nDTSTART:${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nDTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nSUMMARY:${t('calendarEventTitle')}\nDESCRIPTION:${t('successConfirmed')}\nLOCATION:${appointment.location || ''}\nEND:VEVENT\nEND:VCALENDAR`;
                  const blob = new Blob([ics], { type: 'text/calendar' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = t('calendarFileName');
                  a.click();
                  URL.revokeObjectURL(url);
                }}>
                  <img src="/apple-calendar.png" alt="Apple Calendar" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                  Apple / Outlook
                </CalendarButton>
              </CalendarButtons>
            </HeaderCenter>
            <NoPrint>
              <HeaderActionsRight>
                <ActionsRow>
                  <GhostButton onClick={() => window.print()}>{renderIcon(FiPrinter)} <span>{t('actionPrint')}</span></GhostButton>
                  <GhostButton onClick={() => {
                    const data = JSON.stringify({ appointment, deliveryMethod, email }, null, 2);
                    const blob = new Blob([data], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'appointment.json';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}>{renderIcon(FiDownload)} <span>{t('actionDownload')}</span></GhostButton>
                  <GhostButton onClick={() => {
                    const subject = encodeURIComponent(t('appointmentConfirmationSubject'));
                    const body = encodeURIComponent(`${t('location')}: ${appointment.location || '-'}\n${t('date')}: ${appointment.date || '-'}\n${t('time')}: ${appointment.time || '-'}\n`);
                    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                  }}>{renderIcon(FiShare2)} <span>{t('actionShare')}</span></GhostButton>
                </ActionsRow>
              </HeaderActionsRight>
            </NoPrint>
          </UnifiedCardHeader>

          <UnifiedCardBody>
            {/* Removed duplicate row (calendars now in header) */}

            {/* Quick Info Cards */}
            <InfoCardsGrid>
              <InfoCard>
                <InfoCardLabel>
                  {renderIcon(FiMail)} {t('successConfirmationSection')}
                </InfoCardLabel>
                <InfoCardValue>{t('successEmailSentTitle')}</InfoCardValue>
                <InfoCardSubValue>
                  {email ? t('successEmailSentDesc').replace('{email}', email) : t('successEmailSentDescDefault')}
                </InfoCardSubValue>
                <InfoCardHint>
                  {renderIcon(FiInfo)} {t('successCancellationInfo')}
                </InfoCardHint>
              </InfoCard>
              <InfoCard>
                <InfoCardLabel>
                  {renderIcon(FiUserCheck)} {t('sectionBringAlong')}
                </InfoCardLabel>
                <InfoCardValue>{t('bringValidId')}</InfoCardValue>
                <InfoCardSubValue>
                  {t('successIdDocsTitle')}
                </InfoCardSubValue>
                <InfoCardHint>
                  {renderIcon(FiInfo)} {t('successIdDocsDesc')}
                </InfoCardHint>
              </InfoCard>
              <InfoCard>
                <InfoCardLabel>
                  {renderIcon(FiClock)} {t('successTimingSection')}
                </InfoCardLabel>
                <InfoCardValue>{t('successArrivalTitle')}</InfoCardValue>
                <InfoCardSubValue>
                  {t('successArrivalDesc')}
                </InfoCardSubValue>
                <InfoCardHint>
                  {renderIcon(FiInfo)} {t('successArrivalInfo')}
                </InfoCardHint>
              </InfoCard>
            </InfoCardsGrid>

            <SummaryGrid>
              {appointment.location && (
                <SummaryItem>
                  <SummaryLabel>{renderIcon(FiMapPin)} {t('successLocationSection')}</SummaryLabel>
                  <SummaryValue>{appointment.location}</SummaryValue>
                  {appointment.address && (
                    <SummarySubValue>{appointment.address}</SummarySubValue>
                  )}
                </SummaryItem>
              )}
              {appointment.date && (
                <SummaryItem>
                  <SummaryLabel>{renderIcon(FiCalendar)} {t('successDateSection')}</SummaryLabel>
                  <SummaryValue>{appointment.date}</SummaryValue>
                </SummaryItem>
              )}
              {appointment.time && (
                <SummaryItem>
                  <SummaryLabel>{renderIcon(FiClock)} {t('successTimeSection')}</SummaryLabel>
                  <SummaryValue>{appointment.time}</SummaryValue>
                </SummaryItem>
              )}
            </SummaryGrid>

            <NoPrint>
              <ActionsRow style={{ marginTop: '1rem' }}>
              {appointment.location && (
                <PrimaryButton onClick={() => {
                  const defaultPlaceLink = 'https://maps.app.goo.gl/jcAajeZwNTt7iB4h8';
                  const url = (appointment.mapsUrl && appointment.mapsUrl.trim().length > 0)
                    ? appointment.mapsUrl
                    : defaultPlaceLink;
                  window.open(url, '_blank');
                }}>
                  {renderIcon(FiExternalLink)} <span>{t('successOpenInMaps')}</span>
                </PrimaryButton>
              )}
            </ActionsRow>
            </NoPrint>
          </UnifiedCardBody>
        </SectionCard>
        
        {/* Optional friendly info or tips could go here later */}
        
        
      </ContentContainer>
    </Container>
  );
};

export default SuccessPage;